import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../icons/Icon';
import { SarFieldInfoDefinition, SarFieldInfoSection } from './config/SarFieldInfoConfiguration';
import { useSarFieldInfoConfiguration } from './useSarFieldInfoConfiguration';

const renderSectionBadge = (section: SarFieldInfoSection): ReactElement | null => {
  if (!section.badge) {
    return null;
  }
  let backgroundClass: string;
  switch (section.badge.tone) {
    case 'danger':
      backgroundClass = 'bg-[#FFF1F3] text-f-red-2';
      break;
    case 'success':
      backgroundClass = 'bg-[#DDFBEA] text-[#027A48]';
      break;
    case 'warning':
    default:
      backgroundClass = 'bg-[#FFEEDD] text-[#FF7020]';
      break;
  }
  return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${backgroundClass}`}>{section.badge.text}</span>;
};

const SarFieldInfoModalComponent: React.FC<SarFieldInfoModalProps> = ({ visible, definition, onClose }) => {
  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, visible]);

  const content = (
    <div className='fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(25,34,49,0.55)] px-4' onClick={handleOverlayClick}>
      <div className='flex w-full max-w-4xl flex-col rounded-2xl bg-[#F5F7FA] shadow-xl max-h-[85vh] min-h-[40vh]'>
        <div className='flex items-center justify-between bg-main-gray px-6 py-3 rounded-t-xl shrink-0'>
          <span className='text-sm font-semibold text-f-black-2'>{definition.title}</span>
          {/* <button
            type='button'
            aria-label='Kapat'
            className='flex h-8 w-8 items-center justify-center rounded-full bg-f-gray-2 text-f-black-3 transition hover:bg-f-gray-3'
            onClick={onClose}
          >
            <Icon icon='close' size={12} />
          </button> */}
        </div>
        <div className='flex-1 overflow-y-auto rounded-b-2xl bg-white '>
          {definition.sections.map((section, index) => (
            <div
              key={section.id}
              className={`flex items-center gap-x-0x px-6 py-4 ${index !== definition.sections.length - 1 ? 'border-b border-[#E0E4E8]' : ''}`}
            >
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center'>
                <Icon icon={section.icon} size={14} className='text-f-black-3' />
              </div>
              <span className='min-w-[160px] text-sm font-semibold text-f-black-2'>{section.title}</span>
              <span className='flex-1 text-sm leading-6 text-f-black-3 whitespace-pre-line'>{section.description}</span>
              {section.badge ? <div className='ml-auto'>{renderSectionBadge(section)}</div> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!visible) {
    return null;
  }

  return createPortal(content, document.body);
};

export const useSarFieldInfoModal = ({ fieldPath, fieldLabel, fieldType, additionalInfo }: SarFieldInfoModalContent): SarFieldInfoModalHookResult => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const definition = useSarFieldInfoConfiguration({ fieldPath, fieldLabel, fieldType, additionalInfo });

  const openModal = useCallback((): void => {
    setIsVisible(true);
  }, []);

  const closeModal = useCallback((): void => {
    setIsVisible(false);
  }, []);

  const modalElement = useMemo<ReactElement | null>(() => {
    if (!isVisible) {
      return null;
    }

    return <SarFieldInfoModalComponent visible={isVisible} definition={definition} onClose={closeModal} />;
  }, [closeModal, definition, isVisible]);

  return {
    isVisible,
    openModal,
    closeModal,
    modalElement,
  };
};

const defaultTriggerClassName = 'p-0 m-0 border-none bg-transparent hover:cursor-pointer';

const SarFieldInfoButton: React.FC<SarFieldInfoButtonProps> = ({ fieldPath, fieldLabel, fieldType, additionalInfo, trigger }) => {
  const { openModal, modalElement } = useSarFieldInfoModal({ fieldPath, fieldLabel, fieldType, additionalInfo });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      openModal();
    },
    [openModal],
  );

  return (
    <>
      <button type='button' className={defaultTriggerClassName} onClick={handleClick}>
        {trigger}
      </button>
      {modalElement}
    </>
  );
};

interface SarFieldInfoModalProps {
  readonly visible: boolean;
  readonly definition: SarFieldInfoDefinition;
  readonly onClose: () => void;
}

interface SarFieldInfoModalContent {
  readonly fieldPath: string;
  readonly fieldLabel: string;
  readonly fieldType: string;
  readonly additionalInfo?: string;
}

interface SarFieldInfoModalHookResult {
  readonly isVisible: boolean;
  readonly openModal: () => void;
  readonly closeModal: () => void;
  readonly modalElement: React.ReactElement | null;
}

interface SarFieldInfoButtonProps extends SarFieldInfoModalContent {
  readonly trigger?: React.ReactElement;
}

export default SarFieldInfoButton;
